<template>
    <div id="form-list-page">
        <h3 class="title">{{$t("message.metaLeadForms")}}</h3>
        <div class="table-wrapper">
            <SupsisTable
                :columns="getCols()"
                :rows="forms"
                top-pagination
                empty-data-bordered
                :pagination-cursor="pagination"
                :options="{
                    rowStyleOption,
                    clipboardOption,
                    fixedHeader: true,
                    id: 'forms-table',
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>
    </div>
</template>
<script>
import _ from "lodash";
import { mapActions, mapMutations, mapState } from "vuex";
import Vue from "vue";
import log2 from "../../../../utils/log2";
import Modal2 from "../../../../components/common/Modal2.vue";
import moment from "moment";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import SupsisTable from "../../../../components/SupsisTable.vue";
import CommonFilterTool from "../../Chatbot/pages/archive/CommonFilterTool.vue";
import { LEAD_API } from "../../../../api/meta";
import HoverMenuCell from "../../../../components/common/HoverMenuCell.vue";

const log = log2("FormList");
export default {
    name: "FormList",
    components: {
        CommonFilterTool,
        SupsisTable,
        Modal2,
        SupsisButton,
        HoverMenuCell,
    },

    props: {
        channel: {
            type: Object,
            required: true,
        },
        channelId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            searchText: "",
            pagination: {
                page: 1,
                perPage: 10,
                total: 0,
                defaultPageSize: 50,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            loading: true,
            loadingInstance: null,
            forms: [],
            filterMetas: [
                {
                    name: "pageId",
                    label: "Page",
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [],
                },
            ],
            breakdownFilter: [
                {
                    name: "pageId",
                    label: "Page",
                    filterValue: null,
                    materialIcon: "filter_alt",
                    controlType: "select",
                    options: () => [],
                },
            ],
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            siteId: (state) => state.site.site._id,
            user: (state) => state.auth.user,
        }),
    },
    methods: {
        gotoFormLeads(form) {
            console.log("gotoFormLeads", form);
            this.$emit("showFormLeads", form);
            this.$router.push(`/integrations/meta-leads/${this.channel._id}/detail/leads?formId=${form.formId}`);
        },
        getPageName(pageId) {
            const pages = _.get(this.channel.payload, "tokenInfo.pages", []) || [];
            return pages.find((x) => x.id === pageId)?.name || "Unknown Page";
        },
        addFilter(name, value) {
            log.info("addFilter", name);
            const meta = this.filterMetas.find((x) => x.name === name);
            const filter = this.breakdownFilter.find((x) => x.name === name);
            if (filter) {
                this.breakdownFilter = this.breakdownFilter.map((x) => {
                    if (x.name === name) {
                        x.filterValue = value || meta.defaultValue;
                    }
                    return x;
                });
            } else {
                this.breakdownFilter.push({
                    name: meta.name,
                    label: meta.label,
                    filterValue: value || meta.defaultValue,
                    materialIcon: meta.materialIcon,
                    controlType: meta.controlType,
                    options: meta.options,
                });
            }
        },
        removeFilter(name) {
            log.info("removeFilter", name);
            this.breakdownFilter = this.breakdownFilter.filter((x) => x.name !== name);
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            log.info("filterLabelFormatter", { name, value, filter });
            const isSelect = filter.controlType === "select";
            if (isSelect) {
                const option = filter.options().find((x) => x.id === value);
                if (option) {
                    return `${filter.label}: ${option.label}`;
                }
            }
            if (value) {
                return `${filter.label}: ${value}`;
            }
            return filter.label;
        },
        isFilterExists(name) {
            return !!this.breakdownFilter.find((x) => x.name === name);
        },
        getCols() {
            return [
                {
                    key: "name",
                    field: "name",
                    title: this.$t("message.name"),
                    width: "",
                    align: "left",
                },
                {
                    key: "page",
                    field: "pageId",
                    title: this.$t("message.page"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h("span", {}, this.getPageName(row.pageId));
                    },
                },
                {
                    key: "status",
                    field: `status`,
                    title: this.$t("message.status"),
                },
                {
                    key: "question",
                    field: `questions`,
                    title: this.$t("message.questions"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                class: "text-primary",
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            content: row.questions
                                                .map((q) => (q.label + ":").padEnd(50, "_"))
                                                .join("<br>"),
                                        },
                                    },
                                ],
                            },
                            [h("span", row.questions.length + " Questions")]
                        );
                    },
                },
                {
                    key: "leadCount",
                    field: `leadCount`,
                    title: this.$t("message.leadCount"),
                },
                {
                    key: "channelName",
                    field: `channelName`,
                    title: this.$t("message.channel"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.channel?.name || "-"}</span>;
                    },
                },
                {
                    key: "createdAt",
                    field: `createdAt`,
                    title: this.$t("message.createdAt"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{moment(row.createdAt).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    title: this.$t("message.action"),
                    key: "operation",
                    width: "100px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div class="actions">
                                <SupsisButton onClick={() => this.gotoFormLeads(row)} small ghost primary>
                                    {this.$t("message.showLeads")}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ];
        },
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        ...mapMutations({}),

        refresh: _.debounce(function () {
            this.loading = true;
            setTimeout(() => {
                this.loading = false;
            }, 500);
            this.pagination.page = 1;
            this.fetchForms();
        }, 400),

        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
            this.refresh();
        },
        generateFilterParams() {
            return this.breakdownFilter
                .map((filter) => {
                    if (!!filter.filterValue) {
                        return {
                            key: filter.name,
                            value: filter.filterValue,
                            operator: "equals",
                        };
                    }
                    return undefined;
                })
                .filter((x) => !!x);
        },
        async fetchForms() {
            if (!this.channel) return;
            this.loading = true;
            const cursor = {
                offset: (this.pagination.page - 1) * this.pagination.perPage,
                limit: this.pagination.perPage,
            };
            const filters = [
                this.channel
                    ? {
                          key: "channel",
                          value: this.channel._id,
                          operator: "equals",
                      }
                    : undefined,
                (this.searchText || "").trim().length > 0
                    ? {
                          key: "*",
                          value: (this.searchText || "").trim(),
                      }
                    : undefined,
                ...this.generateFilterParams(),
            ].filter((x) => !!x);
            const sort = { createdAt: -1 };
            const forms = await LEAD_API.listForms(filters, cursor, sort)
                .then((res) => res.data)
                .catch((err) => {
                    return { items: [], count: 0 };
                });

            console.log("forms request:", forms);

            this.forms = forms.items;
            this.pagination.total = forms.count;
            this.loading = false;
        },
    },
    async mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".table-wrapper"),
            name: "wave",
        });
        this.loading = !!this.channel && !this.forbidden;
        if (this.loading) this.loadingInstance.show();
        await this.fetchForms();
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        channelId(val) {
            this.searchText = "";
            this.pagination.page = 1;
            this.fetchForms();
        },
        pagination: {
            handler(val, oldVal) {
                const pageSizeChanged = val.perPage !== oldVal.perPage;
                const pageChanged = val.page !== oldVal.page;
                const isChanged = pageSizeChanged || pageChanged;
                if (!isChanged) return;
                if (pageSizeChanged) {
                    this.pagination.page = 1;
                }
                console.log("pagination changed", val);
                this.fetchForms();
            },
            deep: true,
        },
        breakdownFilter: {
            handler(val, oldVal) {
                this.fetchForms();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
#forms-table {
    .ve-table-body-td {
        padding: 0 2px !important;
    }

    .actions {
        display: flex;
        gap: 5px;

        .supsis-button {
            padding: 3px;

            &.instagram {
                width: 25px;
                height: 25px;
            }

            svg {
                width: 20px;
                height: 20px;
            }
        }
    }
}
</style>
<style scoped lang="scss">
#form-list-page {
    margin: 30px;

    .ant-modal-body {
        padding: 24px;
        font-size: 14px;
        line-height: 1.5;
        word-wrap: break-word;
        display: flex !important;
        flex-direction: row !important;
        height: 80px !important;
    }
}
</style>
